import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { Config, createStateSyncMiddleware, initStateWithPrevTab } from "redux-state-sync";
import siteReducer from "./siteSlice";
import userReducer from "./userSlice";
import { siteApiSlice } from "./siteApiSlice";
import contrastSliceReducer from "./contrastSlice";
import adminReportsFilterReducer from "./adminReportsFilterSlice";
import usersListFilterReducer from "./usersListFilterSlice";
import { createSessionPersistConfig } from "./sessionStoragePersist";

// Only sync essential, low-frequency user actions across tabs.
// Broadcasting every "user/" action (e.g. setDistricts, setSchools, setCourses,
// setCourse, setSchool, setDistrict, transition*) causes rapid dispatch loops
// between open tabs (each tab re-dispatches on every new array/object reference
// it receives, which the other tab then re-broadcasts), leading to runaway CPU
// usage and a frozen UI. Keep this list narrow and only add actions that
// represent genuine cross-tab state changes (e.g. login/logout, account info).
const syncedUserActionTypes = [
	"user/resetUserState",
	"user/setIsLoggedIn",
	"user/setUser",
	"user/setAccessibility",
	"user/clearAccessibility",
];

const syncConfig: Config = {
	predicate: (action) => syncedUserActionTypes.includes(action.type),
};

const rootReducer = combineReducers({
	site: siteReducer,
	// The transition/sync flags are blacklisted so they always start from their
	// initialState value: a page load must re-sync the server-side session rather
	// than trust a rehydrated flag, and a tab closed mid-selection must not come
	// back with isSchoolInTransition still true (which renders SelectSchool blank).
	user: persistReducer(
		{
			key: "user",
			storage,
			blacklist: [
				"username",
				"firstName",
				"lastName",
				"initials",
				"isAdminSessionSynced",
				"isCourseInTransition",
				"isSchoolInTransition",
				"isDistrictInTransition",
			],
		},
		userReducer
	),
	[siteApiSlice.reducerPath]: siteApiSlice.reducer,
	contrast: contrastSliceReducer,
	adminReportsFilter: adminReportsFilterReducer,
	usersListFilter: persistReducer(
		createSessionPersistConfig<ReturnType<typeof usersListFilterReducer>>("usersListFilter"),
		usersListFilterReducer
	),
});

export const store = configureStore({
	reducer: rootReducer,
	devTools: process.env.NODE_ENV !== "production",
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({ serializableCheck: false })
			.concat(createStateSyncMiddleware(syncConfig))
			.concat(siteApiSlice.middleware) as unknown as any,
});

initStateWithPrevTab(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export const persistor = persistStore(store);
